import {
  referenceLevelNumber,
  resolveRateOfSaleEntry,
  type ReferenceRosLevel,
  type RosBasis,
  type RateOfSaleEntry,
  type WindowValues,
} from "~/utils/rateOfSale"
import {
  changesDueBy,
  plannedDeliveryDate,
  reviewPeriodDaysFrom,
  type RuleDepartureSchedule,
} from "~/utils/allocationRuleMaths"

export type AllocationStoreInfo = {
  externalId: string | null
  name: string
  country: string | null
  transitTimeDays: number
  blockId: string | null
  locationId: string | null
  deleted: boolean
}

export type AllocationLookups = {
  storeById: Record<string, AllocationStoreInfo>
  warehouseNameById: Record<string, string>
  inTransitByLocationSku: Record<string, number>
}

export type SalesWindows = WindowValues<number>

export type AllocationSkuLocationSales = SalesWindows & {
  rateOfSale: RateOfSaleEntry | null
  /** Days from the last sale to the latest inventory date; null with no sale in the history. */
  lastSoldDaysAgo: number | null
  /** Days with stock after the last sale, or in the whole history when the SKU never sold. */
  stockDaysSinceLastSale: number
}

/** Keyed by skuId. Holds every size of the requested SKUs' variants, not only the requested SKUs. */
export type AllocationSalesMap = Record<
  string,
  { variantId: string; byLocation: Record<string, AllocationSkuLocationSales> }
>

/** extraData fields by SKU id, only the fields the grid asked for. */
export type AllocationSkuExtraDataMap = Record<string, Record<string, unknown>>

export type AllocationInventoryMap = Record<
  string,
  {
    networkOnHand: number
    byLocation: Record<string, number>
    byWarehouse: Record<string, number>
    freeByWarehouse: Record<string, number>
    endBalanceByWarehouse: Record<string, number>
  }
>

export type ReplenishmentSku = {
  variantId: string
  costPrice: number | null
  recommendedRetailPrice: number | null
}

export type ReplenishmentSkuDetails = {
  productId: string
  productName: string
  variantName: string | null
  size: string | null
  category: string | null
  color: string | null
  imageUrl: string | null
}

export type ReplenishmentPageContext = {
  skuById: Record<string, ReplenishmentSku>
  skuDetailsById: Record<string, ReplenishmentSkuDetails>
  packageNameById: Record<string, string | null>
  /** "YYYY-MM-DD"; null on an undated (NOOS) package. */
  packageCutoffById: Record<string, string | null>
  /** "YYYY-MM-DD" by "packageId:skuId", for variants with their own dates. */
  variantCutoffByKey: Record<string, string>
  currency: string
  rateOfSaleWindowDays: number
  noRecentSalesDays: number
  /** The company's Reference ROS chain, in the order the levels are tried. */
  referenceRosLevels: readonly string[]
  /** A line on this Reference ROS level or a later one in the chain warns; null = off. */
  referenceRosWarnLevel: ReferenceRosLevel | null
}

export type ReplenishmentLineRule = RuleDepartureSchedule & { name: string }

type CompactLine = {
  runCreatedAt: number
  skuId: string
  packageId: string
  storeId: string
  warehouseId: string | null
  toLocationId: string
  ruleId: string | null
  runRosBasis?: RosBasis | null
}

const NO_SALES: SalesWindows = { l7: 0, l14: 0, window: 0 }
const NO_WAREHOUSE_STOCK: Record<string, number> = {}
const NO_BLOCK_STOCK: Record<string, number> = {}
const NO_EXTRA_DATA: Record<string, unknown> = {}

type VariantAtLocation = { sales: SalesWindows; rateOfSale: number }

/** Every size of a variant at a location: summed sales and the sum of the sizes' engine rates. */
function variantsByLocation(sales: AllocationSalesMap) {
  const byKey = new Map<string, VariantAtLocation>()
  for (const { variantId, byLocation } of Object.values(sales)) {
    if (!variantId) continue
    for (const [locationId, location] of Object.entries(byLocation)) {
      const key = `${locationId}:${variantId}`
      const variant = byKey.get(key) ?? { sales: { l7: 0, l14: 0, window: 0 }, rateOfSale: 0 }
      variant.sales.l7 += location.l7
      variant.sales.l14 += location.l14
      variant.sales.window += location.window
      variant.rateOfSale += resolveRateOfSaleEntry(location.rateOfSale).rateOfSale
      byKey.set(key, variant)
    }
  }
  return byKey
}

export function inTransitBySku(inTransitByLocationSku: Record<string, number>) {
  const bySku = new Map<string, number>()
  for (const [key, quantity] of Object.entries(inTransitByLocationSku)) {
    const skuId = key.slice(key.indexOf(":") + 1)
    bySku.set(skuId, (bySku.get(skuId) ?? 0) + quantity)
  }
  return bySku
}

/** "BLOCK_1023" reads as "1023". */
export const blockLabel = (blockId: string) => blockId.replace(/^BLOCK_/, "")

function blockByLocationId(storeById: Record<string, AllocationStoreInfo>) {
  const byLocation = new Map<string, string>()
  for (const store of Object.values(storeById)) {
    if (store.deleted || store.blockId == null || store.locationId == null) continue
    byLocation.set(store.locationId, store.blockId)
  }
  return byLocation
}

/** Store stock of one SKU summed per block, over the non-deleted stores in each block. */
export function storeStockByBlock(
  byLocation: Record<string, number>,
  blockByLocation: ReadonlyMap<string, string>,
) {
  const byBlock: Record<string, number> = {}
  for (const [locationId, quantity] of Object.entries(byLocation)) {
    const blockId = blockByLocation.get(locationId)
    if (blockId !== undefined) byBlock[blockId] = (byBlock[blockId] ?? 0) + quantity
  }
  return byBlock
}

export function memoizeByReference<K extends object, V>(fn: (key: K) => V): (key: K) => V {
  const cache = new WeakMap<K, V>()
  return (key) => {
    if (cache.has(key)) return cache.get(key)!
    const value = fn(key)
    cache.set(key, value)
    return value
  }
}

export type LineHydratorSources = {
  lookups: AllocationLookups
  sales: AllocationSalesMap
  inventory: AllocationInventoryMap
  extraData: AllocationSkuExtraDataMap
  page: ReplenishmentPageContext
  ruleById: ReadonlyMap<string, ReplenishmentLineRule>
  now: Date
  skuDetails?: (skuId: string) => ReplenishmentSkuDetails | undefined
}

export function createLineHydrator({
  lookups,
  sales,
  inventory,
  extraData,
  page,
  ruleById,
  now,
  skuDetails,
}: LineHydratorSources) {
  const transitBySku = inTransitBySku(lookups.inTransitByLocationSku)
  const warnIndex =
    page.referenceRosWarnLevel == null
      ? -1
      : page.referenceRosLevels.indexOf(page.referenceRosWarnLevel)
  const warnPosition = warnIndex < 0 ? null : warnIndex + 1
  const variants = variantsByLocation(sales)
  const blockByLocation = blockByLocationId(lookups.storeById)
  const blockStockOf = memoizeByReference((byLocation: Record<string, number>) =>
    storeStockByBlock(byLocation, blockByLocation),
  )
  return <T extends CompactLine>({ runCreatedAt, ...line }: T) => {
    const store = lookups.storeById[line.storeId]
    const sku = page.skuById[line.skuId]
    const details = skuDetails?.(line.skuId) ?? page.skuDetailsById[line.skuId]
    const rule = line.ruleId ? (ruleById.get(line.ruleId) ?? null) : null
    const location = sales[line.skuId]?.byLocation[line.toLocationId]
    const skuSales = location
      ? { l7: location.l7, l14: location.l14, window: location.window }
      : NO_SALES
    const stock = inventory[line.skuId]
    const variant = sku ? variants.get(`${line.toLocationId}:${sku.variantId}`) : undefined
    const transitTimeDays = store?.transitTimeDays ?? 0
    return {
      ...line,
      runCreatedAt: new Date(runCreatedAt),
      productId: details?.productId ?? "",
      variantId: sku?.variantId ?? "",
      productName: details?.productName ?? line.skuId,
      variantName: details?.variantName ?? null,
      size: details?.size ?? null,
      category: details?.category ?? null,
      color: details?.color ?? null,
      imageUrl: details?.imageUrl ?? null,
      extraData: extraData[line.skuId] ?? NO_EXTRA_DATA,
      costPrice: sku?.costPrice ?? null,
      recommendedRetailPrice: sku?.recommendedRetailPrice ?? null,
      currency: page.currency,
      packageName: page.packageNameById[line.packageId] ?? null,
      packageCutoffDate:
        page.variantCutoffByKey[`${line.packageId}:${line.skuId}`] ??
        page.packageCutoffById[line.packageId] ??
        null,
      storeName: store?.name ?? line.storeId,
      storeExternalId: store?.externalId ?? null,
      block: store?.blockId == null ? null : blockLabel(store.blockId),
      market: store?.country ?? null,
      transitTimeDays,
      warehouseName: line.warehouseId
        ? (lookups.warehouseNameById[line.warehouseId] ?? null)
        : null,
      ruleId: rule ? line.ruleId : null,
      ruleName: rule?.name ?? null,
      plannedDeliveryDate: rule ? plannedDeliveryDate(rule, now, transitTimeDays) : null,
      changesDueBy: rule ? changesDueBy(rule, now) : null,
      reviewPeriodDays: rule ? reviewPeriodDaysFrom(rule, now) : null,
      storeStock: stock?.byLocation[line.toLocationId] ?? 0,
      dcStock: line.warehouseId ? (stock?.freeByWarehouse[line.warehouseId] ?? 0) : 0,
      freeStockByWarehouse: stock?.freeByWarehouse ?? NO_WAREHOUSE_STOCK,
      endBalanceByWarehouse: stock?.endBalanceByWarehouse ?? NO_WAREHOUSE_STOCK,
      storeStockByBlock: stock?.byLocation ? blockStockOf(stock.byLocation) : NO_BLOCK_STOCK,
      inTransit: lookups.inTransitByLocationSku[`${line.toLocationId}:${line.skuId}`] ?? 0,
      networkStock: (stock?.networkOnHand ?? 0) + (transitBySku.get(line.skuId) ?? 0),
      rateOfSaleWindowDays: page.rateOfSaleWindowDays,
      noRecentSalesDays: page.noRecentSalesDays,
      rosFallbackLevel: referenceLevelNumber(line.runRosBasis, page.referenceRosLevels),
      rosFallbackLevelCount: page.referenceRosLevels.length,
      referenceRosWarnLevel: warnPosition,
      lastSoldDaysAgo: location?.lastSoldDaysAgo ?? null,
      stockDaysSinceLastSale: location?.stockDaysSinceLastSale ?? 0,
      salesWindowUnits: skuSales.window,
      ...resolveRateOfSaleEntry(location?.rateOfSale ?? null),
      skuSales,
      variantSales: variant?.sales ?? NO_SALES,
      variantRos: variant?.rateOfSale ?? 0,
    }
  }
}

declare const hydrateLine: ReturnType<typeof createLineHydrator>
export type HydratedLine<T extends CompactLine> = ReturnType<typeof hydrateLine<T>>
